import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/auth';
import { formatDistanceToNow } from 'date-fns';

export function SubconsciousPage() {
  const { user } = useAuth();

  const { data: sessions } = useQuery({
    queryKey: ['subconscious-sessions'],
    queryFn: () => api.subconscious.listSessions(),
    enabled: !!user,
  });

  const { data: dreams } = useQuery({
    queryKey: ['dreams'],
    queryFn: () => api.dreams.list(1, 10),
    enabled: !!user,
  });

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h1 className="text-3xl font-display font-bold text-white mb-4">
          Enter the Subconscious
        </h1>
        <p className="text-dream-300 mb-8">
          Sign in to begin exploring your dreams in a new way.
        </p>
        <Link to="/login" className="btn-primary">Login</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-white mb-2">
          The Subconscious
        </h1>
        <p className="text-dream-300">
          Your dreams continue to unfold here. Chat with the characters from your dreams,
          receive notifications at key moments, and unlock deeper access through meditation.
        </p>
      </div>

      {/* Active Sessions */}
      {sessions && sessions.length > 0 && (
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-white mb-4">Active Sessions</h2>
          <div className="grid gap-4">
            {sessions.map((session) => (
              <Link
                key={session.id}
                to="/subconscious/$sessionId"
                params={{ sessionId: session.id }}
                className="card hover:border-dream-600 transition-colors block"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-lg font-medium text-white">
                    {session.dreamScenario || 'Subconscious Session'}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      session.status === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}
                  >
                    {session.status}
                  </span>
                </div>
                {session.dreamCharacters && (
                  <div className="flex gap-2 mb-2">
                    {session.dreamCharacters.slice(0, 3).map((char) => (
                      <span
                        key={char.id}
                        className="text-xs px-2 py-1 bg-purple-900/30 text-purple-300 rounded"
                      >
                        {char.name}
                      </span>
                    ))}
                  </div>
                )}
                {session.currentMilestone && (
                  <p className="text-dream-400 text-sm">
                    Current: {session.currentMilestone}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Start New Session */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Start a New Session</h2>
        {dreams?.items && dreams.items.length > 0 ? (
          <div className="grid gap-4">
            {dreams.items
              .filter((d) => d.status === 'complete')
              .slice(0, 5)
              .map((dream) => (
                <DreamSessionStarter key={dream.id} dream={dream} />
              ))}
            {dreams.items.filter((d) => d.status === 'complete').length === 0 && (
              <div className="card text-center py-8">
                <p className="text-dream-300 mb-4">
                  Your dreams need to be analyzed first before you can explore them in the subconscious.
                </p>
                <Link to="/dreams" className="btn-secondary">
                  View Dreams
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="card text-center py-8">
            <p className="text-dream-300 mb-4">
              Record some dreams first to start exploring them in the subconscious.
            </p>
            <Link to="/dreams/new" className="btn-primary">
              Record a Dream
            </Link>
          </div>
        )}
      </section>

      {/* How it works */}
      <section className="mt-12 card bg-dream-900/30">
        <h2 className="text-lg font-semibold text-white mb-4">How the Subconscious Works</h2>
        <div className="space-y-4 text-dream-200">
          <p>
            When you start a session, your AI "twin" and the characters from your dream begin
            a conversation that continues in the background throughout your day.
          </p>
          <p>
            Local AI models on your network simulate the "background mental processing" that
            happens when you're not actively thinking about a dream - the way a dream can
            suddenly make sense hours later.
          </p>
          <p>
            At key milestones - a revelation, a conflict, a breakthrough - you'll receive a
            push notification. You can then join the conversation or let it continue.
          </p>
          <p className="text-purple-300">
            Complete a meditation session with your Muse headband to unlock deeper access
            and the ability to directly influence the subconscious narrative.
          </p>
        </div>
      </section>
    </div>
  );
}

function DreamSessionStarter({ dream }: { dream: { id: string; rawContent: string; createdAt: string } }) {
  return (
    <div className="card flex items-center justify-between">
      <div className="flex-1 min-w-0 mr-4">
        <p className="text-dream-200 truncate">{dream.rawContent.slice(0, 100)}...</p>
        <p className="text-dream-400 text-sm mt-1">
          {formatDistanceToNow(new Date(dream.createdAt), { addSuffix: true })}
        </p>
      </div>
      <Link
        to="/subconscious"
        search={{ startWith: dream.id }}
        className="btn-secondary text-sm shrink-0"
      >
        Start Session
      </Link>
    </div>
  );
}
